/**
 * Platform utility for detecting the current environment
 */

// Platform types
export type PlatformType = 'web' | 'ios' | 'android' | 'unknown';

// Define interface for Platform object
export interface IPlatform {
  // Properties
  OS: string;
  isWeb: boolean;
  isNative: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  
  // Methods
  getPlatform(): PlatformType;
  isReactNative(): boolean;
  select<T>(obj: {[platform: string]: T}): T;
}

// Check if we're in a browser environment
export const isWeb = (): boolean => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

// Check if we're in React Native
export const isReactNative = (): boolean => {
  return typeof global !== 'undefined' && typeof global.navigator !== 'undefined' && global.navigator.product === 'ReactNative';
};

// Check if we're on iOS
export const isIOS = (): boolean => {
  if (!isReactNative()) return false;
  
  try {
    // @ts-ignore - Platform is a React Native specific API
    const Platform = require('react-native').Platform;
    return Platform.OS === 'ios';
  } catch (e) {
    return false;
  }
};

// Check if we're on Android
export const isAndroid = (): boolean => {
  if (!isReactNative()) return false;
  
  try {
    // @ts-ignore - Platform is a React Native specific API
    const Platform = require('react-native').Platform;
    return Platform.OS === 'android';
  } catch (e) {
    return false;
  }
};

// Get platform name
export const getPlatform = (): PlatformType => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isWeb()) return 'web';
  return 'unknown';
};

// Export a Platform object similar to React Native's Platform
export const Platform: IPlatform = {
  // Properties
  OS: getPlatform() as string,
  isWeb: isWeb(),
  isNative: isReactNative(),
  isIOS: isIOS(),
  isAndroid: isAndroid(),
  
  // Methods
  getPlatform,
  isReactNative, // Export the method for backwards compatibility
  
  /**
   * React Native compatible select method for platform-specific values
   * @param obj Object with keys for different platforms
   * @returns The value for the current platform
   */
  select: function<T>(obj: {[platform: string]: T}): T {
    const platform = getPlatform();
    
    // First try exact match
    if (obj[platform]) {
      return obj[platform];
    }
    
    // Handle ios/android fallback to native
    if ((platform === 'ios' || platform === 'android') && obj.native) {
      return obj.native;
    }
    
    // Finally try default
    if (obj.default) {
      return obj.default;
    }
    
    // Return first value as a last resort
    return Object.values(obj)[0];
  }
};

export default Platform;